import { useState } from 'react';
import { User as UserIcon, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import authService from '@/services/auth';

type Aba = 'conta' | 'seguranca';

export default function AccountSettings() {
  const { user, refreshUser } = useAuth();
  const [aba, setAba] = useState<Aba>('conta');
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    cpf: user?.cpf || '',
    cargo: user?.cargo || '',
    departamento: user?.departamento || '',
  });

  const [formSenha, setFormSenha] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: '',
  });

  const salvarPerfil = async () => {
    setSalvando(true);
    setMensagem(null);
    try {
      await authService.updateProfile(form);
      await refreshUser();
      setMensagem({ tipo: 'success', texto: 'Perfil atualizado com sucesso!' });
    } catch (error) {
      console.error('Erro ao atualizar perfil', error);
      setMensagem({ tipo: 'error', texto: 'Erro ao atualizar perfil' });
    } finally {
      setSalvando(false);
    }
  };

  const alterarSenha = async () => {
    setMensagem(null);
    if (formSenha.new_password !== formSenha.new_password_confirm) {
      setMensagem({ tipo: 'error', texto: 'As senhas não conferem' });
      return;
    }
    setSalvando(true);
    try {
      await authService.changePassword(formSenha);
      setMensagem({ tipo: 'success', texto: 'Senha alterada com sucesso!' });
      setFormSenha({ current_password: '', new_password: '', new_password_confirm: '' });
    } catch (error: any) {
      console.error('Erro ao alterar senha', error);
      const msg = error?.response?.data?.current_password ? 'Senha atual incorreta' : 'Erro ao alterar senha';
      setMensagem({ tipo: 'error', texto: msg });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 rounded-full bg-slate-100 p-1 w-fit">
        <button
          type="button"
          onClick={() => setAba('conta')}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${aba === 'conta' ? 'bg-white text-sky-600 shadow' : 'text-slate-500'}`}
        >
          <UserIcon className="h-4 w-4" />
          Conta
        </button>
        <button
          type="button"
          onClick={() => setAba('seguranca')}
          className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${aba === 'seguranca' ? 'bg-white text-sky-600 shadow' : 'text-slate-500'}`}
        >
          <Lock className="h-4 w-4" />
          Segurança
        </button>
      </div>

      {mensagem && (
        <div className={`rounded-md p-3 text-sm ${mensagem.tipo === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
          {mensagem.texto}
        </div>
      )}

      {aba === 'conta' && (
        <div className="rounded-lg bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-slate-800">Dados da Conta</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Nome</label>
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Sobrenome</label>
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-600">E-mail</label>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Telefone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">CPF</label>
              <input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Cargo</label>
              <input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Departamento</label>
              <input value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={salvarPerfil}
              disabled={salvando}
              className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
            >
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      )}

      {aba === 'seguranca' && (
        <div className="rounded-lg bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-slate-800">Alterar Senha</h2>
          <div className="grid max-w-md grid-cols-1 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Senha atual</label>
              <input type="password" value={formSenha.current_password} onChange={(e) => setFormSenha({ ...formSenha, current_password: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Nova senha</label>
              <input type="password" value={formSenha.new_password} onChange={(e) => setFormSenha({ ...formSenha, new_password: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Confirmar nova senha</label>
              <input type="password" value={formSenha.new_password_confirm} onChange={(e) => setFormSenha({ ...formSenha, new_password_confirm: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={alterarSenha}
              disabled={salvando}
              className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
            >
              {salvando ? 'Salvando...' : 'Alterar Senha'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
